import { useCallback } from 'react';
import axios from 'axios';
import { GATEWAY_HTTP_URL } from '../config/api';
import { useAuth } from '../context/AuthContext';
import { useOrganization } from '../context/OrganizationContext';

// Types mirror the Sente API (/v1) responses forwarded by the gateway's /api/sente proxy.
export interface SenteIdentity {
  id: string;
  name: string | null;
  description?: string | null;
  email: string;
  createdAt: string;
}

export interface SenteAnnotation {
  kind: 'otp' | 'magic_link' | 'other';
  code?: string | null;
  link?: string | null;
  confidence?: number;
}

export interface SenteMessage {
  id: string;
  identityId: string;
  direction: 'inbound' | 'outbound';
  fromAddr: string;
  toAddr: string;
  subject: string | null;
  parsed: { text?: string; html?: string } | null;
  annotation: SenteAnnotation | null;
  deliveryStatus: string | null;
  archived: boolean;
  createdAt: string;
}

export interface SenteRun {
  id: string;
  type: 'register' | 'login' | 'instruct' | 'connect';
  status: 'queued' | 'running' | 'awaiting_verification' | 'blocked' | 'completed' | 'failed';
  registrationId: string | null;
  error: { code: string; detail?: string } | null;
  result: { answer?: string; username?: string } | null;
  liveViewUrl: string | null;
  gatewaySessionId: string | null;
  createdAt: string;
  endedAt: string | null;
}

// One row of GET /v1/runs (the list). Flat + joined (identity email, app URL) — deliberately not the
// same shape as SenteRun above, which is the single-run view with the live-view/session fields.
export interface SenteRunSummary {
  id: string;
  type: SenteRun['type'];
  status: SenteRun['status'];
  error: SenteRun['error'];
  createdAt: string;
  endedAt: string | null;
  identityId: string;
  identityEmail: string;
  registrationId: string | null;
  appUrl: string | null; // null when the run has no registration (instruct)
}

// GET /v1/audit-events — the insert-only trail of credential/session/connection access.
export interface SenteAuditEvent {
  id: string;
  action: string;
  subjectType: string | null;
  subjectId: string | null;
  meta: Record<string, unknown> | null;
  createdAt: string;
}

// GET /v1/usage — plan caps + consumption for the current billing period.
export interface SenteUsage {
  planId: string;
  emails: { used: number; cap: number; periodStart: string; periodEnd: string };
  identities: { used: number; cap: number };
  accounts: { used: number };
  runs: { used: number; cap: number };
}

export interface SenteRegistration {
  id: string;
  identityId: string;
  appUrl: string;
  // 'created' = Sente registered a new account; 'connected' = the customer owns it and delegated access.
  kind?: 'created' | 'connected';
  username: string | null;
  status: 'pending' | 'active' | 'failed' | 'disabled'; // 'disabled' = a revoked connection
  revokedAt?: string | null;
  createdAt: string;
  latestRun?: SenteRun | null; // present on list responses only
}

export interface SenteCredentials {
  username: string | null;
  password: string | null;
  origin: string | null; // 'generated' | 'supplied' — where the values came from
}

export interface SenteWebhook {
  id: string;
  url: string;
  events: string[];
  identityId: string | null;
  createdAt: string;
}

export class SenteApiError extends Error {
  status: number;
  code?: string;
  constructor(status: number, message: string, code?: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

/**
 * Fetch helper for the gateway's /api/sente/* proxy (Sente AgentAccount data).
 * Same auth convention as every other hook: Auth0 bearer + x-organization-id.
 * Callers must gate on `currentOrganization` (request throws without it).
 */
export function useSente() {
  const { getToken } = useAuth();
  const { currentOrganization } = useOrganization();

  const request = useCallback(
    async <T>(method: 'GET' | 'POST' | 'DELETE', path: string, body?: unknown): Promise<T> => {
      if (!currentOrganization) throw new SenteApiError(400, 'Organization required');
      const token = await getToken();
      try {
        const res = await axios.request<T>({
          method,
          url: `${GATEWAY_HTTP_URL}/api/sente${path}`,
          data: body,
          headers: {
            Authorization: `Bearer ${token}`,
            'x-organization-id': currentOrganization.id,
            'Content-Type': 'application/json',
          },
        });
        return res.data;
      } catch (err: any) {
        const data = err.response?.data;
        // Sente 400s carry a zod issues array — surface the first message, not raw JSON.
        const msg =
          typeof data?.error === 'string'
            ? data.error
            : Array.isArray(data?.error)
              ? data.error[0]?.message ?? 'Invalid request'
              : data?.error
                ? JSON.stringify(data.error)
                : err.message;
        throw new SenteApiError(err.response?.status ?? 0, msg, data?.code);
      }
    },
    [getToken, currentOrganization],
  );

  return {
    ready: Boolean(currentOrganization),
    listIdentities: useCallback(() => request<SenteIdentity[]>('GET', '/identities'), [request]),
    getIdentity: useCallback((id: string) => request<SenteIdentity>('GET', `/identities/${id}`), [request]),
    deleteIdentity: useCallback((id: string) => request<void>('DELETE', `/identities/${id}`), [request]),
    createIdentity: useCallback(
      (input: { name?: string; localPart?: string; onConflict?: 'suffix' | 'error' }) =>
        request<SenteIdentity>('POST', '/identities', input),
      [request],
    ),
    // `identityId: null` → every identity's messages (still org-scoped server-side via the join).
    listMessages: useCallback(
      (
        identityId: string | null,
        opts: { direction?: 'inbound' | 'outbound'; kind?: string; limit?: number; includeArchived?: boolean } = {},
      ) => {
        const qs = new URLSearchParams(identityId ? { identityId } : {});
        if (opts.direction) qs.set('direction', opts.direction);
        if (opts.kind) qs.set('kind', opts.kind);
        if (opts.limit) qs.set('limit', String(opts.limit));
        if (opts.includeArchived) qs.set('includeArchived', 'true');
        return request<SenteMessage[]>('GET', `/messages?${qs.toString()}`);
      },
      [request],
    ),
    getMessage: useCallback((id: string) => request<SenteMessage>('GET', `/messages/${id}`), [request]),
    archiveMessage: useCallback((id: string) => request<SenteMessage>('POST', `/messages/${id}/archive`, {}), [request]),
    unarchiveMessage: useCallback(
      (id: string) => request<SenteMessage>('POST', `/messages/${id}/unarchive`, {}),
      [request],
    ),
    sendMessage: useCallback(
      (input: { identityId: string; to: string; subject: string; text?: string; inReplyTo?: string }) =>
        request<SenteMessage>('POST', '/messages', input),
      [request],
    ),
    listRegistrations: useCallback(
      (identityId?: string) =>
        request<SenteRegistration[]>('GET', `/registrations${identityId ? `?identityId=${identityId}` : ''}`),
      [request],
    ),
    getRegistration: useCallback((id: string) => request<SenteRegistration>('GET', `/registrations/${id}`), [request]),
    // Reveals the vaulted plaintext credentials (retrievable by design; the server audits every read).
    getCredentials: useCallback(
      (id: string) => request<SenteCredentials>('GET', `/registrations/${id}/credentials`),
      [request],
    ),
    createRegistration: useCallback(
      (input: {
        identityId?: string; // omitted → the server auto-provisions the backing identity
        appUrl: string;
        credentials?: { username?: string; password?: string };
        confirmBeforeSubmit?: boolean;
      }) => request<{ registration: SenteRegistration; run: SenteRun }>('POST', '/registrations', input),
      [request],
    ),
    loginRegistration: useCallback(
      (id: string) => request<{ registration: SenteRegistration; run: SenteRun }>('POST', `/registrations/${id}/login`, {}),
      [request],
    ),
    // Connect — delegated access to an account the customer already owns (credentials required).
    // identityId omitted → the server auto-provisions the backing identity.
    createConnection: useCallback(
      (input: { identityId?: string; appUrl: string; credentials: { username: string; password: string; totpSeed?: string } }) =>
        request<{ connection: SenteRegistration; run: SenteRun }>('POST', '/connections', input),
      [request],
    ),
    listConnections: useCallback(
      (identityId?: string) =>
        request<SenteRegistration[]>('GET', `/connections${identityId ? `?identityId=${identityId}` : ''}`),
      [request],
    ),
    // Revoke keeps the vault (reconnect re-enables); delete purges the stored credentials.
    revokeConnection: useCallback((id: string) => request<SenteRegistration>('POST', `/connections/${id}/revoke`, {}), [request]),
    deleteConnection: useCallback((id: string) => request<void>('DELETE', `/connections/${id}`), [request]),
    getRun: useCallback((id: string) => request<SenteRun>('GET', `/runs/${id}`), [request]),
    // Run history across the org (newest first) — the per-account `latestRun` only shows the last one.
    listRuns: useCallback(
      (opts: { identityId?: string; status?: string; type?: string; limit?: number } = {}) => {
        const qs = new URLSearchParams();
        if (opts.identityId) qs.set('identityId', opts.identityId);
        if (opts.status) qs.set('status', opts.status);
        if (opts.type) qs.set('type', opts.type);
        if (opts.limit) qs.set('limit', String(opts.limit));
        return request<SenteRunSummary[]>('GET', `/runs?${qs.toString()}`);
      },
      [request],
    ),
    listAuditEvents: useCallback(
      (opts: { action?: string; limit?: number } = {}) => {
        const qs = new URLSearchParams();
        if (opts.action) qs.set('action', opts.action);
        if (opts.limit) qs.set('limit', String(opts.limit));
        return request<SenteAuditEvent[]>('GET', `/audit-events?${qs.toString()}`);
      },
      [request],
    ),
    getUsage: useCallback(() => request<SenteUsage>('GET', '/usage'), [request]),
    getRunRecording: useCallback(
      (id: string) => request<{ status: string; recordingUrl: string | null }>('GET', `/runs/${id}/recording`),
      [request],
    ),
    resumeRun: useCallback((id: string) => request<SenteRun>('POST', `/runs/${id}/resume`, {}), [request]),
    abortRun: useCallback((id: string) => request<SenteRun>('POST', `/runs/${id}/abort`, {}), [request]),
    interveneRun: useCallback((id: string) => request<SenteRun>('POST', `/runs/${id}/intervene`, {}), [request]),
    listWebhooks: useCallback(() => request<SenteWebhook[]>('GET', '/webhooks'), [request]),
    createWebhook: useCallback(
      (input: { url: string; events: string[]; identityId?: string }) =>
        request<SenteWebhook & { secret: string }>('POST', '/webhooks', input),
      [request],
    ),
    deleteWebhook: useCallback((id: string) => request<void>('DELETE', `/webhooks/${id}`), [request]),
  };
}
